import * as T from 'three';
import { Gait, LEGS } from './Gait';

const cream = new T.MeshStandardMaterial({ color: '#f4e2bb', roughness: .94, flatShading: true });
const chestnut = new T.MeshStandardMaterial({ color: '#a95e38', roughness: .85, flatShading: true });
const dark = new T.MeshStandardMaterial({ color: '#3b3434', roughness: 1, flatShading: true });
const white = new T.MeshStandardMaterial({ color: '#fff3d6', roughness: 1 });
const teal = new T.MeshStandardMaterial({ color: '#2e6968', roughness: 1 });
const up = new T.Vector3(0, 1, 0);
function box(parent: T.Object3D, size: number[], at: number[], mat: T.Material) {
  const m = new T.Mesh(new T.BoxGeometry(...size as [number,number,number]), mat);
  m.position.set(...at as [number,number,number]); m.castShadow = true; m.receiveShadow = true; parent.add(m); return m;
}
function ellipsoid(parent: T.Object3D, scale: number[], at: number[], mat: T.Material) {
  const m = new T.Mesh(new T.IcosahedronGeometry(1, 1), mat);
  m.scale.set(...scale as [number,number,number]); m.position.set(...at as [number,number,number]);
  m.castShadow = true; parent.add(m); return m;
}
function segment(parent: T.Object3D, radius: number, mat: T.Material) {
  const m = new T.Mesh(new T.CylinderGeometry(radius * .8, radius, 1, 7), mat); m.castShadow = true; parent.add(m); return m;
}
const direction = new T.Vector3();
const bendDirection = new T.Vector3();
const supportHip = new T.Vector3();
const LEG_REACH = 1.27; // two .64m bones, retaining a non-singular knee bend
function link(mesh: T.Mesh, a: T.Vector3, b: T.Vector3) {
  direction.subVectors(b, a); mesh.position.copy(a).add(b).multiplyScalar(.5);
  mesh.scale.y = direction.length(); mesh.quaternion.setFromUnitVectors(up, direction.normalize());
}
function label(text: string, width: number, height: number, bg = '#f6e9cb', fg = '#344d46') {
  const c = document.createElement('canvas'); c.width = 512; c.height = 160;
  const ctx = c.getContext('2d')!; ctx.fillStyle = bg; ctx.fillRect(0,0,512,160);
  ctx.fillStyle = fg; ctx.font = 'bold 82px sans-serif'; ctx.textAlign = 'center'; ctx.textBaseline = 'middle'; ctx.fillText(text,256,85);
  const tex = new T.CanvasTexture(c); tex.colorSpace = T.SRGBColorSpace;
  return new T.Mesh(new T.PlaneGeometry(width,height), new T.MeshBasicMaterial({ map: tex, side: T.DoubleSide }));
}
export class World {
  scene = new T.Scene(); camera = new T.PerspectiveCamera(38,1,.1,180);
  horse = new T.Group(); torso = new T.Group(); sun = new T.DirectionalLight('#fff3cf', 3.0);
  private tail = new T.Group();
  private limbs = LEGS.map((l) => {
    const bandMat = new T.MeshStandardMaterial({color:l.color, emissive:l.color, emissiveIntensity:0});
    return { upper:segment(this.scene,.115,chestnut), lower:segment(this.scene,.072,cream),
      hoof:box(this.scene,[.23,.16,.19],[0,0,0],dark), band:box(this.scene,[.19,.11,.21],[0,0,0],bandMat), mat:bandMat,
      hip:new T.Vector3(), knee:new T.Vector3(), foot:new T.Vector3(), lastSteps:0 };
  });
  private dust = Array.from({ length:24 }, () => {
    const mesh = new T.Mesh(new T.IcosahedronGeometry(.07,0), new T.MeshBasicMaterial({color:'#e9ca96',transparent:true,opacity:0,depthWrite:false}));
    this.scene.add(mesh); return { mesh, age:1, index:0 };
  });
  private dustIndex = 0; private followX = 0;
  constructor() {
    this.scene.background = new T.Color('#c9dcd8'); this.scene.fog = new T.Fog('#c9dcd8',35,115);
    this.scene.add(new T.HemisphereLight('#eff9f1','#a68159',2.1));
    this.sun.position.set(-3,10,6); this.sun.castShadow = true; this.sun.shadow.mapSize.set(1024,1024);
    Object.assign(this.sun.shadow.camera,{left:-8,right:8,top:8,bottom:-8,near:.1,far:35});
    this.sun.shadow.normalBias = .035; this.scene.add(this.sun,this.sun.target);
    const ground = new T.Mesh(new T.PlaneGeometry(180,130).rotateX(-Math.PI/2),new T.MeshStandardMaterial({color:'#9eae80',roughness:1}));
    ground.position.set(30,-.025,-25); ground.receiveShadow = true; this.scene.add(ground);
    const lane= new T.Mesh(new T.PlaneGeometry(78,4.5).rotateX(-Math.PI/2),new T.MeshStandardMaterial({color:'#cda775',roughness:1}));
    lane.position.set(30,.001,0); lane.receiveShadow = true; this.scene.add(lane);
    for(const side of [-1,1]) {
      for(let x=-6;x<66;x+=3) { box(this.scene,[.09,.9,.09],[x,.45,side*2.45],cream); }
      for(const y of [.35,.7]) box(this.scene,[75,.055,.06],[30,y,side*2.45],cream);
    }
    for(let x=0;x<=50;x+=10) {
      const sign = label(x === 0 ? 'START' : x+' m',1.65,.52); sign.position.set(x,1.45,-2.6); this.scene.add(sign);
      box(this.scene,[.06,1.2,.06],[x,.6,-2.6],dark);
      if(x>0) box(this.scene,[.045,.012,4.5],[x,.008,0],cream);
    }
    for(const x of [0,50]) for(let i=0;i<12;i++) for(let j=0;j<2;j++) {
      box(this.scene,[.28,.012,.36],[x+j*.28,.015,-2+i*.36], (i+j)%2 ? dark : white);
    }
    for(const z of [-2.4,2.4]) box(this.scene,[.16,3.5,.16],[50,1.75,z],teal);
    box(this.scene,[.2,.15,4.8],[50,3.45,0],teal);
    const finish = label('FINISH',3.6,.6,'#2e6968','#fff3d6'); finish.position.set(50,3.05,0); finish.rotation.y=Math.PI/2; this.scene.add(finish);
    // Deterministic scenery, no downloaded assets or runtime randomness.
    const foliage = new T.MeshStandardMaterial({color:'#5e826b',flatShading:true,roughness:1});
    const lightFoliage = new T.MeshStandardMaterial({color:'#8fa479',flatShading:true,roughness:1});
    for(let i=0;i<32;i++) {
      const x = i*3.6-18, z = -7 - (i*7%13), height = 1.2+(i%4)*.4;
      box(this.scene,[.16,height,.16],[x,height/2,z],chestnut);
      ellipsoid(this.scene,[.9,height*.9,.85],[x,height*1.25,z],i%2?foliage:lightFoliage);
    }
    const hill = new T.MeshStandardMaterial({color:'#9bb3a0',roughness:1,flatShading:true});
    for(let i=0;i<12;i++) ellipsoid(this.scene,[7+i%3,3+i%4,6],[i*11-20,0,-30-i%3*6],hill);
    for(let i=0;i<85;i++) {
      const x=(i*17%76)-5,z=(i%2 ? 1:-1)*(3.2+(i*3%9));
      const tuft = new T.Mesh(new T.ConeGeometry(.14,.32,3),foliage); tuft.position.set(x,.12,z); this.scene.add(tuft);
    }
    this.scene.add(this.horse); this.horse.add(this.torso);
    ellipsoid(this.torso,[1.0,.44,.43],[0,.05,0],chestnut);
    ellipsoid(this.torso,[.5,.4,.39],[-.58,.06,0],chestnut);
    ellipsoid(this.torso,[.44,.44,.36],[.63,.08,0],chestnut);
    const neck=ellipsoid(this.torso,[.28,.69,.27],[.85,.53,0],chestnut); neck.rotation.z=-.34;
    ellipsoid(this.torso,[.4,.29,.26],[1.19,1.05,0],chestnut);
    ellipsoid(this.torso,[.34,.21,.23],[1.51,.93,0],cream);
    box(this.torso,[.12,.34,.19],[1.4,1.12,0],white).rotation.z=-.5;
    for(const z of [-.19,.19]) {
      ellipsoid(this.torso,[.09,.24,.08],[1.06,1.43,z],chestnut).rotation.z=.2;
      ellipsoid(this.torso,[.055,.065,.035],[1.29,1.16,z*1.36],dark);
      ellipsoid(this.torso,[.018,.023,.014],[1.31,1.18,z*1.5],white);
      ellipsoid(this.torso,[.05,.035,.018],[1.7,.94,z*1.12],dark);
    }
    for(let i=0;i<7;i++) ellipsoid(this.torso,[.12,.17,.16],[.48+i*.07,.37+i*.11,0],dark);
    const blanket=box(this.torso,[.82,.10,.88],[-.12,.43,0],teal);
    blanket.rotation.z=-.025;
    box(this.torso,[.3,.15,.61],[-.12,.53,0],dark);
    this.tail.position.set(-.95,.15,0); this.torso.add(this.tail);
    const tail=ellipsoid(this.tail,[.16,.52,.15],[-.13,-.33,0],dark); tail.rotation.z=-.25;
    this.camera.position.set(4.3,3.4,8); this.camera.lookAt(.5,1,0);
  }
  update(dt: number, gait: Gait, pressed: readonly boolean[], reduced: boolean) {
    const down=gait.phase==='fallen' || (gait.phase==='lost' && gait.fallAge>0);
    const smooth=1-Math.exp(-dt*10);
    // Present the kicking legs toward the follow camera instead of hiding them behind the torso.
    const targetRoll=down ? -1.36 : gait.roll;
    const bob= !reduced && gait.speed>.1 ? Math.sin(gait.time*19)*.018 : 0;
    this.horse.position.x=gait.x;
    this.horse.position.y += ((down?.43:gait.height+bob)-this.horse.position.y)*smooth;
    this.horse.rotation.x += (targetRoll-this.horse.rotation.x)*smooth;
    this.horse.rotation.z += ((down?-.13:gait.pitch)-this.horse.rotation.z)*smooth;
    this.horse.updateMatrixWorld(true);
    if(!down) {
      // Smoothed height/bob and pitch/roll are only a preferred body pose.
      // Reconcile that pose against every actual support BEFORE solving IK:
      // lower the common body, never pull planted endpoints off the ground.
      // Rotation fixes each hip's x/z; the reach sphere gives its maximum y.
      // Keeping this constraint after smoothing also covers landing and low FPS.
      let drop=0;
      for(let i=0;i<4;i++) if(gait.legs[i].contact) {
        const def=LEGS[i];
        supportHip.set(def.x,-.08,def.z).applyMatrix4(this.horse.matrixWorld);
        const dx=supportHip.x-gait.legs[i].plantX, dz=supportHip.z-def.z;
        const maxHipY=.10+Math.sqrt(Math.max(0,LEG_REACH*LEG_REACH-dx*dx-dz*dz));
        drop=Math.max(drop,supportHip.y-maxHipY);
      }
      if(drop>0) {
        this.horse.position.y-=drop;
        this.horse.updateMatrixWorld(true);
      }
    }
    for(let i=0;i<4;i++) {
      const def=LEGS[i], state=gait.legs[i], limb=this.limbs[i];
      limb.hip.set(def.x,-.08,def.z).applyMatrix4(this.horse.matrixWorld);
      if(down) {
        limb.foot.set(def.x+state.reach,-.85+state.lift*.32,def.z).applyMatrix4(this.horse.matrixWorld);
        limb.foot.y=Math.max(.10,limb.foot.y);
      } else {
        const reach = state.contact ? state.plantX-gait.x-def.x : state.reach;
        // Swing clearance and support labels share simulation contact; even a
        // short commanded lift must visibly leave its old ground anchor.
        const clearance = state.contact ? 0 : state.lift * .20;
        limb.foot.set(gait.x+def.x+reach,.10+clearance,def.z);
      }
      // Two rigid segment IK, preserving both bone lengths instead of stretching to ground.
      direction.subVectors(limb.foot,limb.hip); const length=T.MathUtils.clamp(direction.length(),.15,LEG_REACH);
      direction.normalize(); limb.foot.copy(limb.hip).addScaledVector(direction,length);
      limb.knee.copy(limb.hip).addScaledVector(direction,length/2);
      const bend=Math.sqrt(Math.max(0,.64*.64-length*length/4));
      const bendDir=bendDirection.set(-direction.y,direction.x,0).normalize().multiplyScalar(i<2?-1:1);
      limb.knee.addScaledVector(bendDir,bend);
      link(limb.upper,limb.hip,limb.knee); link(limb.lower,limb.knee,limb.foot);
      limb.hoof.position.copy(limb.foot); limb.hoof.rotation.x=this.horse.rotation.x;
      limb.band.position.copy(limb.foot).lerp(limb.knee,.27); limb.band.quaternion.copy(limb.lower.quaternion);
      limb.mat.emissiveIntensity=pressed[i]?.5:0;
      if(state.steps>limb.lastSteps && !reduced) {
        for(let j=0;j<3;j++) { const d=this.dust[this.dustIndex++%this.dust.length]; d.age=0; d.index=j; d.mesh.position.copy(limb.foot); }
      }
      limb.lastSteps=state.steps;
    }
    this.tail.rotation.x=reduced?0:Math.sin(gait.time*5)*.13;
    for(const d of this.dust) {
      d.age+=dt; d.mesh.visible=d.age<.5;
      if(d.mesh.visible) { d.mesh.position.x-=dt*(.3+d.index*.15); d.mesh.position.y+=dt*.25; d.mesh.position.z+=dt*(d.index-1)*.4;
        d.mesh.scale.setScalar(1+d.age*3); d.mesh.material.opacity=(1-d.age*2)*.55; }
    }
    this.followX+=(gait.x-this.followX)*(1-Math.exp(-dt*5));
    const mobile=this.camera.aspect<1, distance=mobile?11:8;
    this.camera.position.set(this.followX+distance*.34,3.2+distance*.04,distance);
    this.camera.lookAt(this.followX+.5,mobile?1.05:.95,0);
    this.sun.position.set(this.followX-3,10,6); this.sun.target.position.set(this.followX,0,0);
  }
  legPositions() { return this.limbs.map(l => l.hoof.position.toArray()); }
  reset(gait:Gait) { this.followX=gait.x; this.horse.position.set(gait.x,gait.height,0); this.horse.rotation.set(0,0,0); this.limbs.forEach(l=>l.lastSteps=0); this.dust.forEach(d=>{d.age=1;}); }
}
